/**
 * "Authorization" issues are REST errors caused by invalid user credentials.
 *    These are issues that should be solvable by an end-user updating/fixing
 *    the credentials that they have provided.
 */
import BasicError, { authCodes } from '../errors'

/**
 * Function that checks a REST response action for Link authorization issues.
 * @param  {Object} response A REST response object.
 * @return {BasicError|undefined}
 */
export function linkAuthorization (response) {
  if (!response.error) {
    return
  }

  /*
   * A SPiDR REST request will return
   *  - '403 Forbidden', with body 'statusCode: 4' for bad password,
   *  - '403 Forbidden', with html body (which is thrown away) for bad username.
   *
   * The SPiDR REST API document (v4.7, May 2019) says it will return '403 Forbidden'
   *    with other statusCodes for numerous untestable scenarios. Some of these are
   *    for credential/auth issues, others are for unrelated issues.
   * Authorization related:
   *    - 27 (generic auth failure), 61 (credentials issue), 65 (token expired)
   * Unrelated:
   *    - 29 (device not authorized), 35 (service not enabled), 37 (invalid value),
   *      45 (invalid app_name), 46 (invalid CallMe token), 62 (account is disabled),
   *      63 (account is locked), 64 (invalid CallMe realm)
   */
  // Status codes considered to be "auth issues".
  const authIssues = [4, 27, 61, 65]

  const statusCode = getStatusCode(response)

  /*
   * A case where Link REST request will return '401 Unauthorized'
   */
  if (response.result.code === 401) {
    return new BasicError({
      code: authCodes.INVALID_CREDENTIALS,
      message: 'Authorization failed with server. Please check credentials.'
    })
  }

  /*
   * Define a Link authorization issue to be:
   *  - a '403 Forbidden', with no body or with a specific statusCode.
   */
  if (response.result.code === 403 && (response.body === undefined || authIssues.includes(statusCode))) {
    let message = 'Authorization failed with server. Please check credentials.'
    if (statusCode) {
      message += ` Status code: ${statusCode}`
    }
    return new BasicError({
      code: authCodes.INVALID_CREDENTIALS,
      message: message
    })
  }
}

/**
 * Function that checks a REST response action for UC authorization issues.
 *    UC includes both SPiDR and CIM REST requests.
 * @param  {Object} response A REST response object.
 * @return {BasicError|undefined}
 */
export function ucAuthorization (response) {
  /*
   * A CIM REST request will return
   *    - '401 Login Please' (not 401 Unauthorized) with no body for credential issues.
   *    - '401 Login Please', with body message "expired token" for token issues.
   *
   * SPiDR will return '401 Unauthorized' for non-auth issues though,
   *    (statusCode 35 for "service not enabled").
   */

  /*
   * Define a UC authorization issue to be:
   *  - a '401 Login Please'.
   */
  const result = response.result
  if (result.code === 401 && result.message === 'Login Please') {
    let message = 'Authorization failed with server. Please check credentials.'

    const statusCode = getStatusCode(response)

    if (statusCode) {
      message += ` Status code: ${statusCode}`
    }

    return new BasicError({
      code: authCodes.INVALID_CREDENTIALS,
      message
    })
  }
}

/**
 * Function that checks a REST response action for CPaaS authorization issues.
 * @param  {Object} response A REST response object.
 * @return {BasicError|undefined}
 */
export function cpaasAuthorization (response) {
  /*
   * A CPaaS REST request will return '401 Unauthorized' for authorization issues.
   */
  if (response.result.code === 401) {
    return new BasicError({
      code: authCodes.INVALID_CREDENTIALS,
      message: 'Authorization failed with server. Please check credentials.'
    })
  }
}

/**
 * Helper function for parsing a statusCode from a SPiDR REST response body.
 * @param  {Object} response
 * @return {number|undefined}
 */
function getStatusCode (response) {
  let statusCode
  /*
   * Parse the statusCode out from the body.
   */
  if (response.body) {
    if (response.body.statusCode) {
      // In some cases (eg. KAA-1937), the statusCode is at the top-level of the body.
      statusCode = response.body.statusCode
    } else {
      // In most cases, the statusCode is inside another object. The name of this
      //    parameter is different depending which service the request was for,
      //    so search for it.
      Object.values(response.body).forEach(value => {
        if (value.statusCode) {
          statusCode = value.statusCode
        }
      })
    }
  }
  return statusCode
}
